import { useEffect, useState } from 'react';
import { AlertCircle, Search, UserMinus, UserPlus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import { ApiError } from '@/lib/api';
import { formatInviteDate, personDisplayName } from './companyOnboarding';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import {
  platformRoleLabel,
  usePlatformUsers,
  useSetUserActive,
  type PlatformUser,
} from './hooks/usePlatformPeopleQueries';

/**
 * Значение с задержкой — чтобы поиск не дёргал сервер на каждый символ.
 *
 * Локальная копия: такие же `useDebounced` уже живут в `FieldSearchPanel` и
 * `PesticidesCatalogPage`. Сводить три копии в общий хук — правка shared-слоя,
 * это зона head-web; здесь не расширяю диф ради неё, но фиксирую долг.
 */
function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/**
 * `/platform-admin/users` — все люди платформы (AK-572).
 *
 * Пункт меню вёл в 404, хотя ручки живы с самого начала (аудит 12.08).
 *
 * Блокировка здесь ГЛОБАЛЬНАЯ: закрывает вход во все компании сразу и рвёт
 * живые сессии. Это не то же, что «закрыть доступ» в конкретной компании —
 * то живёт в раскрытой строке компании и трогает одно членство. Поэтому в
 * подписи кнопки прямо сказано «во всех компаниях», а не просто
 * «заблокировать»: цена ошибки разная.
 */
export function PlatformAdminUsersPage() {
  const [search, setSearch] = useState('');
  // Дебаунс: ключ запроса — строка поиска, и без задержки каждый нажатый
  // символ тянул бы с сервера до 500 строк. Поиск здесь — главный контрол
  // экрана, так что 300 мс окупаются сразу.
  const debouncedSearch = useDebouncedValue(search, 300);
  const [onlyBlocked, setOnlyBlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Закрытие доступа во ВСЕХ компаниях подтверждаем: строки списка выглядят
  // одинаково, соседняя — другой человек, а последствие — выход из системы
  // прямо сейчас. Возврат доступа безобиден и подтверждения не требует.
  const [banTarget, setBanTarget] = useState<PlatformUser | null>(null);
  const [banError, setBanError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const usersQuery = usePlatformUsers(debouncedSearch, onlyBlocked);
  const setActive = useSetUserActive();
  const users = usersQuery.data ?? [];

  const toggle = async (u: PlatformUser, blocked: boolean) => {
    setError(null);
    setNotice(null);
    if (blocked) setBanError(null);
    setPendingId(u.id);
    try {
      await setActive.mutateAsync({
        userId: u.id,
        blocked,
        reason: blocked ? 'Заблокирован администратором платформы' : undefined,
      });
      if (blocked) setBanTarget(null);
      setNotice(
        blocked
          ? `${personDisplayName(u)} потерял доступ во всех компаниях — сессии разорваны.`
          : `${personDisplayName(u)} снова может входить.`,
      );
    } catch (err) {
      const text =
        err instanceof ApiError
          ? err.detail || 'Не удалось изменить доступ'
          : 'Не удалось изменить доступ';
      if (blocked) setBanError(text);
      else setError(text);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <PlatformAdminLayout>
      <div className="flex h-full flex-col">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Пользователи</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Все люди платформы. Блокировка отсюда закрывает вход во все компании
            сразу — доступ к одной компании закрывают в её строке на «Компаниях».
          </p>
        </div>

        <Card className="mt-4 flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border px-4 py-3">
            <div className="relative min-w-[240px] flex-1">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск по почте или имени…"
                className="pl-8"
                aria-label="Поиск пользователей"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="only-blocked"
                checked={onlyBlocked}
                onCheckedChange={(v) => setOnlyBlocked(v === true)}
              />
              <Label htmlFor="only-blocked" className="text-sm font-normal">
                Только с закрытым доступом
              </Label>
            </div>
          </div>

          {error && (
            <div className="border-b border-border px-4 py-2 text-sm text-destructive" role="alert">
              {error}
            </div>
          )}

          {/* Успех — строкой на экране: `toast.success` в проекте сегодня
              только пишет в консоль, то есть был бы невидимым. */}
          {notice && (
            <div
              className="border-b border-border px-4 py-2 text-sm text-muted-foreground"
              role="status"
            >
              {notice}
            </div>
          )}

          {usersQuery.isError && (
            <div className="flex items-start gap-2 border-b border-border bg-destructive/5 px-4 py-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <div className="flex-1">Не удалось загрузить пользователей</div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void usersQuery.refetch()}
              >
                Повторить
              </Button>
            </div>
          )}

          {/* Список без виртуализации намеренно: сервер отдаёт не больше
              500 строк (сузить можно поиском), а виртуальная таблица здесь
              стоила бы измеряемой высоты в обмен на ничто. */}
          <ScrollArea className="min-h-0 flex-1">
            <div className="divide-y divide-border/60 px-4">
              {usersQuery.isLoading && (
                <div className="space-y-2 py-3">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              )}

              {usersQuery.isSuccess && users.length === 0 && (
                <p className="py-6 text-sm text-muted-foreground">
                  {search || onlyBlocked
                    ? 'По этому запросу никого нет'
                    : 'Пользователей пока нет'}
                </p>
              )}

              {users.map((u) => (
                <div
                  key={u.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3"
                >
                  <div className="min-w-0 flex-1 basis-[240px]">
                    <p className="truncate text-sm font-medium text-foreground">
                      {personDisplayName(u)}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {u.email}
                    </p>
                  </div>

                  <div className="basis-[170px]">
                    {u.platform_role ? (
                      <Badge variant="accent-brand">
                        {platformRoleLabel(u.platform_role)}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        не сотрудник платформы
                      </span>
                    )}
                  </div>

                  <div className="basis-[110px] text-xs text-muted-foreground">
                    компаний: {u.tenants_count}
                  </div>

                  <div className="basis-[150px] text-xs text-muted-foreground">
                    {u.last_login_at
                      ? `входил ${formatInviteDate(u.last_login_at)}`
                      : 'не входил'}
                  </div>

                  {u.is_active ? (
                    <Badge variant="primary">Доступ открыт</Badge>
                  ) : (
                    <Badge variant="destructive">Доступ закрыт</Badge>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    /* Гасим только свою строку: общий isPending блокировал
                       весь список на время любой одной мутации. */
                    disabled={pendingId === u.id}
                    onClick={() => {
                      if (u.is_active) {
                        setBanError(null);
                        setBanTarget(u);
                      } else {
                        void toggle(u, false);
                      }
                    }}
                  >
                    {u.is_active ? (
                      <>
                        <UserMinus className="h-4 w-4" />
                        Закрыть во всех компаниях
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4" />
                        Вернуть доступ
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        </Card>

        <p className="mt-3 shrink-0 text-[11px] text-muted-foreground">
          Показываем до 500 человек — сузьте поиском, если нужного не видно.
        </p>

        <ConfirmActionDialog
          open={banTarget !== null}
          title="Закрыть доступ во всех компаниях?"
          description={
            banTarget
              ? `${personDisplayName(banTarget)} (${banTarget.email}) выйдет из системы сразу и не сможет войти ни в одну компанию. Доступ возвращается этой же кнопкой.`
              : ''
          }
          confirmLabel="Закрыть доступ"
          destructive
          pending={setActive.isPending}
          error={banError}
          onConfirm={() => {
            if (banTarget) void toggle(banTarget, true);
          }}
          onOpenChange={(open) => {
            if (!open) {
              setBanTarget(null);
              setBanError(null);
            }
          }}
        />
      </div>
    </PlatformAdminLayout>
  );
}

export default PlatformAdminUsersPage;
